/** Pure helpers for the shop, staff and subscription screens (unit-tested). */
import { ApiError, type Schemas } from '../../api/client';

// --- API errors -------------------------------------------------------------------------

/**
 * Field messages from an API validation error: `body.currencyCode` → `currencyCode`. Only the
 * first message per field is kept.
 */
export function apiFieldErrors(error: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(error instanceof ApiError)) return out;
  for (const d of error.details) {
    const field = d.path.replace(/^(body|query|params)\./, '');
    if (field && !(field in out)) out[field] = d.message;
  }
  return out;
}

/** Copies API field errors into a react-hook-form; returns true when at least one matched a field. */
export function applyFieldErrors(
  error: unknown,
  fields: readonly string[],
  setError: (name: never, e: { type: string; message: string }) => void,
): boolean {
  const map = apiFieldErrors(error);
  let matched = false;
  for (const [field, message] of Object.entries(map)) {
    if (fields.includes(field)) {
      setError(field as never, { type: 'server', message });
      matched = true;
    }
  }
  return matched;
}

// --- document numbering -----------------------------------------------------------------

export type DocType = Schemas['DocumentNumbering']['docType'];

export const DOC_TYPE_LABELS: Record<DocType, { label: string; group: string }> = {
  QUOTATION: { label: 'Quotation', group: 'Sales' },
  SALES_ORDER: { label: 'Sales order', group: 'Sales' },
  DELIVERY: { label: 'Delivery note', group: 'Sales' },
  SALES_INVOICE: { label: 'Sales invoice', group: 'Sales' },
  SALES_RETURN: { label: 'Sales return', group: 'Sales' },
  CREDIT_NOTE: { label: 'Credit note', group: 'Sales' },
  PURCHASE_ORDER: { label: 'Purchase order', group: 'Purchasing' },
  GOODS_RECEIPT: { label: 'Goods receipt', group: 'Purchasing' },
  SUPPLIER_INVOICE: { label: 'Supplier bill', group: 'Purchasing' },
  PURCHASE_RETURN: { label: 'Purchase return', group: 'Purchasing' },
  DEBIT_NOTE: { label: 'Debit note', group: 'Purchasing' },
  CUSTOMER_RECEIPT: { label: 'Customer receipt', group: 'Payments' },
  SUPPLIER_PAYMENT: { label: 'Supplier payment', group: 'Payments' },
  REFUND: { label: 'Refund', group: 'Payments' },
  STOCK_ADJUSTMENT: { label: 'Stock adjustment', group: 'Stock' },
  STOCK_COUNT: { label: 'Stock count', group: 'Stock' },
  POS_SESSION: { label: 'POS register session', group: 'Point of Sale' },
  POS_ORDER: { label: 'POS receipt', group: 'Point of Sale' },
};

export const NUMBER_TOKENS = [
  { token: '{prefix}', help: 'The prefix, e.g. INV' },
  { token: '{series}', help: 'Series (MAIN, or a device series)' },
  { token: '{yyyy}', help: 'Year, 4 digits' },
  { token: '{yy}', help: 'Year, 2 digits' },
  { token: '{mm}', help: 'Month, 2 digits' },
  { token: '{seq:5}', help: 'Running number, zero-padded' },
] as const;

/**
 * Renders a numbering pattern the way the backend does (tokens `{prefix} {series} {yyyy} {yy}
 * {mm} {seq:N}`), for a live preview while editing.
 */
export function renderNumberPattern(
  pattern: string,
  v: { prefix: string; series: string; nextValue: string; date?: Date },
): string {
  const d = v.date ?? new Date();
  const yyyy = String(d.getFullYear());
  return pattern.replace(/\{(prefix|series|yyyy|yy|mm|seq(?::(\d{1,2}))?)\}/g, (_, token, pad) => {
    switch (token as string) {
      case 'prefix':
        return v.prefix;
      case 'series':
        return v.series;
      case 'yyyy':
        return yyyy;
      case 'yy':
        return yyyy.slice(2);
      case 'mm':
        return String(d.getMonth() + 1).padStart(2, '0');
      default:
        return v.nextValue.padStart(Number(pad ?? 0), '0');
    }
  });
}

/** Problems with a pattern the user is typing (empty list = fine). */
export function patternProblems(pattern: string): string[] {
  const problems: string[] = [];
  if (!pattern.trim()) return ['Enter a pattern'];
  if (!/\{seq(?::\d{1,2})?\}/.test(pattern))
    problems.push('Include {seq} or {seq:N} so every number is unique');
  const unknown = pattern
    .match(/\{[^}]*\}/g)
    ?.filter((t) => !/^\{(prefix|series|yyyy|yy|mm|seq(:\d{1,2})?)\}$/.test(t));
  if (unknown?.length) problems.push(`Unknown token ${unknown.join(', ')}`);
  if (pattern.length > 64) problems.push('At most 64 characters');
  return problems;
}

// --- permissions ------------------------------------------------------------------------

export type Permission = Schemas['PermissionCatalog']['data'][number];

export interface PermissionGroup {
  module: string;
  label: string;
  permissions: Permission[];
}

const MODULE_ORDER = [
  'dashboard',
  'sales',
  'quotations',
  'customers',
  'purchases',
  'suppliers',
  'products',
  'inventory',
  'payments',
  'ledger',
  'accounts',
  'journal',
  'reports',
  'reminders',
  'staff',
  'roles',
  'settings',
  'subscription',
  'devices',
  'sync',
  'audit',
];

export function moduleLabel(module: string): string {
  const s = module.replace(/[_-]+/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Groups the permission catalog by module in a stable, business-first order. */
export function groupPermissions(catalog: Permission[]): PermissionGroup[] {
  const map = new Map<string, Permission[]>();
  for (const p of catalog) {
    const list = map.get(p.module) ?? [];
    list.push(p);
    map.set(p.module, list);
  }
  const rank = (m: string) => {
    const i = MODULE_ORDER.indexOf(m);
    return i === -1 ? MODULE_ORDER.length : i;
  };
  return [...map.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([module, permissions]) => ({ module, label: moduleLabel(module), permissions }));
}

/** 'all' | 'some' | 'none' of a group's permissions are in `selected`. */
export function groupState(group: PermissionGroup, selected: ReadonlySet<string>) {
  const n = group.permissions.filter((p) => selected.has(p.key)).length;
  return n === 0 ? 'none' : n === group.permissions.length ? 'all' : 'some';
}

export function toggleGroup(group: PermissionGroup, selected: ReadonlySet<string>): Set<string> {
  const next = new Set(selected);
  const on = groupState(group, selected) !== 'all';
  for (const p of group.permissions) {
    if (on) next.add(p.key);
    else next.delete(p.key);
  }
  return next;
}

/** Starting points for a new role; only keys present in the catalog are applied. */
export const ROLE_TEMPLATES = [
  {
    id: 'cashier',
    name: 'Cashier',
    description: 'Sells at the counter and takes payments.',
    permissions: [
      'dashboard.view',
      'customers.view',
      'customers.create',
      'products.view',
      'inventory.view',
      'sales.view',
      'sales.create',
      'sales.invoice',
      'payments.view',
      'payments.create',
    ],
  },
  {
    id: 'storekeeper',
    name: 'Storekeeper',
    description: 'Receives goods, counts and adjusts stock.',
    permissions: [
      'dashboard.view',
      'products.view',
      'inventory.view',
      'inventory.adjust',
      'inventory.count',
      'inventory.transfer',
      'purchases.view',
      'purchases.receive',
      'sales.view',
      'sales.deliver',
      'suppliers.view',
    ],
  },
  {
    id: 'manager',
    name: 'Manager',
    description: 'Runs the shop day to day, without staff or settings.',
    permissions: [
      'dashboard.view',
      'customers.view',
      'customers.create',
      'customers.update',
      'suppliers.view',
      'suppliers.create',
      'suppliers.update',
      'products.view',
      'products.create',
      'products.update',
      'products.view_cost',
      'products.update_price',
      'inventory.view',
      'inventory.adjust',
      'inventory.count',
      'inventory.transfer',
      'quotations.view',
      'quotations.create',
      'quotations.update',
      'quotations.convert',
      'sales.view',
      'sales.create',
      'sales.confirm',
      'sales.deliver',
      'sales.invoice',
      'sales.cancel',
      'sales.return',
      'sales.discount',
      'purchases.view',
      'purchases.create',
      'purchases.confirm',
      'purchases.receive',
      'purchases.bill',
      'purchases.return',
      'payments.view',
      'payments.create',
      'payments.allocate',
      'ledger.view',
      'reports.view',
      'reports.financial',
      'reports.export',
      'reminders.view',
      'staff.view',
      'settings.view',
    ],
  },
  {
    id: 'accountant',
    name: 'Accountant',
    description: 'Payments, ledgers and financial reports.',
    permissions: [
      'dashboard.view',
      'customers.view',
      'suppliers.view',
      'products.view',
      'products.view_cost',
      'sales.view',
      'purchases.view',
      'purchases.bill',
      'payments.view',
      'payments.create',
      'payments.allocate',
      'payments.refund',
      'payments.void',
      'ledger.view',
      'journal.view',
      'accounts.manage',
      'reports.view',
      'reports.financial',
      'reports.export',
    ],
  },
] as const;

/** Readable action name for a permission key: `products.update_price` → `Update price`. */
export function permissionAction(key: string): string {
  const action = key.split('.').slice(1).join(' ').replace(/_/g, ' ');
  return action ? action.charAt(0).toUpperCase() + action.slice(1) : key;
}

// --- subscription -----------------------------------------------------------------------

export type SubscriptionStatus = NonNullable<Schemas['SubscriptionStatus']>;

export interface SubscriptionView {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'primary';
  /** 0–1 share of the paid period still left. */
  remaining: number;
  headline: string;
  detail: string;
  isTrial: boolean;
}

const DAY = 86_400_000;

export function plural(n: number, word: string): string {
  return `${String(n)} ${word}${n === 1 ? '' : 's'}`;
}

/** What the subscription card shows for a status. */
export function subscriptionView(s: SubscriptionStatus, now = Date.now()): SubscriptionView {
  const start = new Date(s.startsAt).getTime();
  const end = new Date(s.paidThrough).getTime();
  const total = Math.max(end - start, DAY);
  const remaining = Math.min(1, Math.max(0, (end - now) / total));
  const isTrial = !s.plan;
  const days = Math.max(0, s.daysRemaining);

  if (s.suspended)
    return {
      label: 'Suspended',
      tone: 'error',
      remaining: 0,
      headline: 'Your shop is suspended',
      detail: 'Contact PackBytes to resume. Your data is safe.',
      isTrial,
    };
  switch (s.state) {
    case 'LOCKED':
      return {
        label: 'Expired',
        tone: 'error',
        remaining: 0,
        headline: 'Your subscription has expired',
        detail: 'Redeem an activation code or contact PackBytes to unlock your shop.',
        isTrial,
      };
    case 'GRACE':
      return {
        label: 'Grace period',
        tone: 'error',
        remaining: 0,
        headline: `${plural(Math.max(0, s.daysUntilLock), 'day')} until lock`,
        detail: 'Your paid period has ended. Renew now to keep working without interruption.',
        isTrial,
      };
    case 'EXPIRING_SOON':
      return {
        label: isTrial ? 'Trial ending' : 'Expiring soon',
        tone: 'warning',
        remaining,
        headline: `${plural(days, 'day')} left`,
        detail: 'Renew soon — redeem an activation code to extend your subscription.',
        isTrial,
      };
    default:
      return {
        label: isTrial ? 'Free trial' : 'Active',
        tone: isTrial ? 'primary' : 'success',
        remaining,
        headline: `${plural(days, 'day')} left`,
        detail: isTrial
          ? 'Enjoy every feature during your trial.'
          : 'Everything is up to date. Thank you for being with us.',
        isTrial,
      };
  }
}

export type SubscriptionNotice = Schemas['SubscriptionNotice'];

export interface NoticeView {
  severity: 'info' | 'warning' | 'error';
  title: string;
  body: string;
}

/**
 * The app-wide banner for the trial, an ending subscription and the grace period; null when there
 * is nothing to say (paid and not ending soon) or the shop is locked (the locked screen shows instead).
 */
export function noticeView(s: SubscriptionNotice): NoticeView | null {
  if (s.locked || s.suspended) return null;
  const left = Math.max(0, s.daysRemaining);
  const toLock = Math.max(0, s.daysUntilLock);
  const lockIn = toLock === 0 ? 'today' : `in ${plural(toLock, 'day')}`;
  if (s.state === 'GRACE')
    return {
      severity: 'error',
      title: s.onTrial
        ? `Your free trial has ended. You’re in a ${String(s.graceDays)}-day grace period`
        : `Your subscription has ended. You’re in a ${String(s.graceDays)}-day grace period`,
      body: `${plural(toLock, 'day')} left: the shop locks ${lockIn} unless it is renewed. Contact PackBytes or redeem an activation code.`,
    };
  const ends = left === 0 ? 'today' : left === 1 ? 'tomorrow' : `in ${plural(left, 'day')}`;
  if (s.state === 'EXPIRING_SOON')
    return {
      severity: 'warning',
      title: s.onTrial ? `Your free trial ends ${ends}` : `Your subscription ends ${ends}`,
      body: `After that you get a ${String(s.graceDays)}-day grace period, then the shop locks. Renew to keep working without interruption.`,
    };
  if (s.onTrial)
    return {
      severity: 'info',
      title: `Free trial: ${plural(left, 'day')} left`,
      body: 'Every feature is open during the trial. Contact PackBytes when you are ready to subscribe.',
    };
  return null;
}

export type SupportContact = Schemas['SupportContact'];

/** Ways to reach PackBytes, in the order shown, with ready-made links. */
export function contactLinks(
  c: SupportContact,
): { kind: 'phone' | 'whatsapp' | 'email' | 'website'; label: string; href: string }[] {
  const digits = (v: string) => v.replace(/[^0-9+]/g, '');
  return [
    c.phone ? { kind: 'phone' as const, label: c.phone, href: `tel:${digits(c.phone)}` } : null,
    c.whatsapp
      ? {
          kind: 'whatsapp' as const,
          label: c.whatsapp,
          href: `https://wa.me/${digits(c.whatsapp).replace('+', '')}`,
        }
      : null,
    c.email ? { kind: 'email' as const, label: c.email, href: `mailto:${c.email}` } : null,
    {
      kind: 'website' as const,
      label: c.website.replace(/^https?:\/\//, '').replace(/\/$/, ''),
      href: c.website,
    },
  ].filter((l) => l !== null);
}

/** Formats activation-code input as `XXXX-XXXX-XXXX-XXXX` (Crockford Base32, upper case). */
export function formatActivationCode(input: string): string {
  const raw = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 16);
  return raw.match(/.{1,4}/g)?.join('-') ?? '';
}

/** True when the input holds a full 16-character activation code. */
export function isCompleteActivationCode(input: string): boolean {
  return /^[0-9A-Z]{4}(-[0-9A-Z]{4}){3}$/.test(formatActivationCode(input));
}

/** Friendly text for activation-code errors from `POST /subscription/redeem`. */
export function redeemErrorText(code: string): string {
  switch (code) {
    case 'INVALID_CODE':
      return 'This code isn’t valid for your shop. Check it and try again.';
    case 'CODE_EXPIRED':
      return 'This code has expired. Ask your provider for a new one.';
    case 'CODE_REVOKED':
      return 'This code was cancelled by your provider.';
    case 'CODE_ALREADY_REDEEMED':
      return 'This code has already been used.';
    case 'RATE_LIMITED':
      return 'Too many attempts. Wait 15 minutes and try again.';
    default:
      return 'The code could not be redeemed. Try again in a moment.';
  }
}

export type SubscriptionEventKind = Schemas['SubscriptionEvent']['kind'];

export const SUBSCRIPTION_EVENT_LABELS: Record<SubscriptionEventKind, string> = {
  CREATED: 'Shop created',
  RENEWED: 'Renewed',
  EXTENDED: 'Extended by support',
  CODE_REDEEMED: 'Activation code redeemed',
  SUSPENDED: 'Suspended',
  RESUMED: 'Resumed',
  UPDATED: 'Subscription updated',
  CORRECTED: 'Corrected by support',
  REVERSED: 'Removed by support',
};

// --- passwords & invitations ------------------------------------------------------------

/** 0 (empty) to 4 (strong): length plus character variety. */
export function passwordStrength(password: string): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score++;
  return Math.max(1, Math.min(4, score)) as 1 | 2 | 3 | 4;
}

export const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'] as const;

/** The invitation token from an invite link: `#token=…` (as the API builds it) or `?token=…`. */
export function readInviteToken(search: string, hash: string): string | null {
  const fromHash = new URLSearchParams(hash.replace(/^#/, '')).get('token');
  const fromQuery = new URLSearchParams(search).get('token');
  return (fromHash ?? fromQuery)?.trim() || null;
}

export function invitationErrorText(code: string): { title: string; body: string } {
  switch (code) {
    case 'INVITATION_EXPIRED':
      return {
        title: 'This invitation has expired',
        body: 'Ask the shop owner to send you a new invitation link.',
      };
    case 'INVITATION_USED':
      return {
        title: 'This invitation was already used',
        body: 'Your account is ready — sign in with your email or phone and password.',
      };
    case 'INVITATION_REVOKED':
      return {
        title: 'This invitation was withdrawn',
        body: 'Ask the shop owner if you should receive a new one.',
      };
    default:
      return {
        title: 'This invitation link is not valid',
        body: 'Check that you opened the full link from your message, or ask for a new one.',
      };
  }
}
